import { ApiClientConfig, ApiClientPlatformConfig } from "../api-client";

/**
 * Default timeout for postMessage operations in milliseconds.
 * If a response is not received within this time, the operation will resolve with null.
 */
const DEFAULT_TIMEOUT = 5000;

/**
 * Storage keys used for LTI PostMessage communication.
 * These keys follow the IMS LTI PostMessage Storage specification:
 * @see https://www.imsglobal.org/spec/lti-pm-s/v0p1
 */
const STORAGE_KEYS = {
	/** Access token for API authentication */
	ACCESS_TOKEN: "lti.verity.accessToken",
	/** Account identifier for the current user */
	ACCOUNT_ID: "lti.verity.accountId",
	/** Refresh token for obtaining new access tokens */
	REFRESH_TOKEN: "lti.verity.refreshToken",
	/** Timestamp of the last token refresh operation */
	LOCAL_REFRESHED_TIME: "lti.verity.localRefreshTime",
} as const;

/**
 * Base interface for all LTI tool messages.
 * All messages must include a unique message_id for correlation.
 */
interface LtiMessageBase {
	message_id: string;
}

/**
 * Message format for storing data in the LTI tool.
 * Follows the IMS LTI PostMessage Storage specification for put_data operations.
 */
interface LtiToolPutRequest<V> extends LtiMessageBase {
	subject: "lti.put_data";
	key: string;
	value: V;
}

/**
 * Message format for retrieving data from the LTI tool.
 * Follows the IMS LTI PostMessage Storage specification for get_data operations.
 */
interface LtiToolGetRequest extends LtiMessageBase {
	subject: "lti.get_data";
	key: string;
}

/**
 * Message format for the response to a {@link LtiToolPutRequest}.
 * Follows the IMS LTI PostMessage Storage specification for put_data operations.
 */
interface LtiPlatformPutResponse<V> extends LtiMessageBase {
	subject: "lti.put_data.response";
	key: string;
	value: V;
	error?: unknown;
}

/**
 * Message format for the response to a {@link LtiToolGetRequest}.
 * Follows the IMS LTI PostMessage Storage specification for get_data operations.
 */
interface LtiPlatformGetResponse<V> extends LtiMessageBase {
	subject: "lti.get_data.response";
	key: string;
	value: V;
	error?: unknown;
}

/**
 * Generates a unique message ID for postMessage communication.
 * Format: lti_msg_<timestamp>_<random>
 *
 * @returns A unique string identifier
 */
function generateMessageId(): string {
	return `lti_msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Sends a message to the target window and waits for a response.
 * Implements the IMS LTI PostMessage Storage specification for message exchange.
 *
 * @param targetWindow - Window to send the message to
 * @param targetOrigin - Origin that the target window must have
 * @param message - Message to send (either put_data or get_data)
 * @param timeout - Maximum time to wait for a response
 * @returns Promise that resolves with the response data or null if timeout/error
 */
async function sendMessage<V>(
	targetWindow: Window,
	targetOrigin: string,
	message: LtiToolPutRequest<V> | LtiToolGetRequest,
	timeout: number,
): Promise<V | undefined> {
	return new Promise((resolve) => {
		// Set up timeout handler
		const timeoutId = setTimeout(() => {
			window.removeEventListener("message", messageHandler);
			resolve(undefined);
		}, timeout);

		// Handle incoming messages
		function messageHandler(event: MessageEvent) {
			// Verify Origin
			const isValidOrigin = event.origin === targetOrigin;
			if (isValidOrigin) {
				// Verify Response Structure
				const data = event.data as unknown;
				if (data !== null) {
					const isValidResponseStructure =
						typeof data === "object" &&
						"subject" in data &&
						typeof data.subject === "string" &&
						"message_id" in data &&
						typeof data.message_id === "string";

					if (!isValidOrigin || !isValidResponseStructure) {
						return;
					}

					// Valid response, handle cleanup and resolve
					window.removeEventListener("message", messageHandler);
					clearTimeout(timeoutId);
					const verifiedData = data as LtiPlatformGetResponse<V> | LtiPlatformPutResponse<V>;

					if (verifiedData.error) {
						resolve(undefined);
					} else {
						resolve(verifiedData.value);
					}
				}
			}
		}

		// Set up message listener and send the message
		window.addEventListener("message", messageHandler);
		targetWindow.postMessage(message, targetOrigin);
	});
}

/**
 * Creates an ApiClientConfig implementation that uses LTI PostMessage for token storage.
 * This adapter enables secure communication with an LTI Tool Provider through the
 * postMessage API, following the IMS LTI PostMessage Storage specification.
 *
 * @param targetWindow - Window object to send messages to (e.g., parent frame)
 * @param targetOrigin - Origin that must match the target window
 * @param config - Platform configuration including Verity and Lockdown settings
 * @param timeout - Maximum time to wait for responses (default: 5000ms)
 * @returns An ApiClientConfig implementation using LTI PostMessage
 */
export function createLtiPostMessageAdapter(
	targetWindow: Window,
	targetOrigin: string,
	config: ApiClientPlatformConfig,
	timeout = DEFAULT_TIMEOUT,
): ApiClientConfig {
	return {
		...config,
		getRefreshToken: async () => {
			return sendMessage<string>(
				targetWindow,
				targetOrigin,
				{
					subject: "lti.get_data",
					message_id: generateMessageId(),
					key: STORAGE_KEYS.REFRESH_TOKEN,
				},
				timeout,
			);
		},
		setRefreshToken: async (refreshToken) => {
			await sendMessage<string | undefined>(
				targetWindow,
				targetOrigin,
				{
					subject: "lti.put_data",
					message_id: generateMessageId(),
					key: STORAGE_KEYS.REFRESH_TOKEN,
					value: refreshToken,
				},
				timeout,
			);
		},
		getAccountId: async () => {
			return sendMessage<string>(
				targetWindow,
				targetOrigin,
				{
					subject: "lti.get_data",
					message_id: generateMessageId(),
					key: STORAGE_KEYS.ACCOUNT_ID,
				},
				timeout,
			);
		},
		setAccountId: async (accountId) => {
			await sendMessage<string | undefined>(
				targetWindow,
				targetOrigin,
				{
					subject: "lti.put_data",
					message_id: generateMessageId(),
					key: STORAGE_KEYS.ACCOUNT_ID,
					value: accountId,
				},
				timeout,
			);
		},
		getAccessToken: async () => {
			return sendMessage<string>(
				targetWindow,
				targetOrigin,
				{
					subject: "lti.get_data",
					message_id: generateMessageId(),
					key: STORAGE_KEYS.ACCESS_TOKEN,
				},
				timeout,
			);
		},
		setAccessToken: async (accessToken) => {
			await sendMessage<string | undefined>(
				targetWindow,
				targetOrigin,
				{
					subject: "lti.put_data",
					message_id: generateMessageId(),
					key: STORAGE_KEYS.ACCESS_TOKEN,
					value: accessToken,
				},
				timeout,
			);
		},
		getLocalRefreshedTime: async () => {
			return sendMessage<number>(
				targetWindow,
				targetOrigin,
				{
					subject: "lti.get_data",
					message_id: generateMessageId(),
					key: STORAGE_KEYS.LOCAL_REFRESHED_TIME,
				},
				timeout,
			);
		},
		setLocalRefreshedTime: async (timestamp) => {
			await sendMessage<number | undefined>(
				targetWindow,
				targetOrigin,
				{
					subject: "lti.put_data",
					message_id: generateMessageId(),
					key: STORAGE_KEYS.LOCAL_REFRESHED_TIME,
					value: timestamp,
				},
				timeout,
			);
		},
		isEnvironmentSupported: () => {
			try {
				// Check if we're in a browser environment
				if (typeof window === "undefined") {
					return false;
				}

				// Check if target window is accessible
				if (!targetWindow || targetWindow.closed) {
					return false;
				}

				// Test if postMessage exists
				if (!targetWindow.postMessage) {
					return false;
				}

				return true;
			} catch (error) {
				return false;
			}
		},
	};
}
